import type { CccOrdering, CccOrderingRule, CccOrderingScope } from '../../hooks/useGetSettings';

/**
 * Shared value helpers for custom CCC ordering. The Ordering section's table and
 * the rule screen read and write the same `cccOrdering` field, so the shape
 * conversion and the labelling rules live here rather than once per surface.
 */

export type CccOrderingMode = 'always' | 'never' | 'custom';

export const cccOrderingMode = (value: CccOrdering | undefined): CccOrderingMode => {
    if (value === 'never') return 'never';
    if (value === 'always' || value == null) return 'always';
    return 'custom';
};

/**
 * `'all'` is only ever the stored sentinel (or a missing side). An empty list
 * is kept as an empty list: the API matches nothing against it, and reading it
 * as `'all'` would widen the rule the next time it is saved.
 */
const toScope = (scope: CccOrderingScope | undefined): CccOrderingScope => {
    if (scope === 'all' || scope == null) return 'all';
    if (!Array.isArray(scope)) return 'all';
    return scope
        .map((entry) => ({ _id: entry?._id?.toString() ?? '', name: entry?.name ?? '' }))
        .filter((entry) => !!entry._id);
};

/**
 * The rules a stored value carries. Custom ordering shipped as a single
 * `{ makes, carriers }` object, which is one rule, so it is read as one rather
 * than needing a migration.
 */
export const toCccOrderingRules = (value: CccOrdering | undefined): Array<CccOrderingRule> => {
    if (value === 'always' || value === 'never' || value == null) return [];
    const rules = Array.isArray(value) ? value : [value];
    return rules
        .filter((rule): rule is CccOrderingRule => !!rule)
        .map((rule) => ({ makes: toScope(rule.makes), carriers: toScope(rule.carriers) }));
};

/**
 * What to write back. A single rule is stored as the `{ makes, carriers }`
 * object custom ordering already uses, so shops with one rule keep the shape
 * they have today; the list is only written once there is a second rule.
 */
export const toStoredCccOrdering = (rules: Array<CccOrderingRule>): CccOrdering =>
    rules.length === 1 ? rules[0] : rules;

const sortScope = (scope: CccOrderingScope): CccOrderingScope =>
    scope === 'all' ? 'all' : [...scope].sort((a, b) => a._id.localeCompare(b._id));

/**
 * Stable shape for draft ↔ saved comparisons: the list form either way, with
 * each side's entries in a fixed order, so a re-ordered response or the
 * object-vs-list storage choice is not read as an edit.
 */
export const normalizeCccOrdering = (value: CccOrdering | undefined): CccOrdering => {
    const mode = cccOrderingMode(value);
    if (mode !== 'custom') return mode === 'never' ? 'never' : 'always';
    return toCccOrderingRules(value).map((rule) => ({
        makes: sortScope(rule.makes),
        carriers: sortScope(rule.carriers),
    }));
};

const NONE_LABEL = 'None';

/** "Dodge, Jeep +2", the all-label, or "None". Matches the other rule tables' cells. */
export const cccScopeLabel = (scope: CccOrderingScope, allLabel: string): string => {
    if (scope === 'all') return allLabel;
    const names = scope.map((entry) => entry.name).filter((name) => !!name);
    if (names.length === 0) return NONE_LABEL;
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
};

/** Every name on one side of a rule, for the cell's tooltip. */
export const cccScopeNames = (scope: CccOrderingScope, allLabel: string): Array<string> => {
    if (scope === 'all') return [allLabel];
    const names = scope.map((entry) => entry.name).filter((name) => !!name);
    return names.length > 0 ? names : [NONE_LABEL];
};
